import { useCallback, useEffect, useMemo, useState } from 'react';

import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Breadcrumb, Button } from 'antd';
import { Link, useNavigate } from 'react-router';

import {
  useDeleteSchoolSpeakerMutation,
  useGetSchoolSpeakersQuery,
  useReorderSchoolSpeakersMutation,
} from '@/api/endpoints/schools/schoolSubjectionParams/speakers';
import { DeleteConfirmationModal, DraggableTable, FetchDataWrapper } from '@/shared/components';
import { ROUTES, TABLE_TRANSLATIONS } from '@/shared/consts';
import { useApiRequest } from '@/shared/hooks';

import { CreateSpeakerModal } from './components/Modals/CreateSpeakerModal';
import { EditSpeakerModal } from './components/Modals/EditSpeakerModal';
import { getColumnsData } from './helpers';
import styles from './SchoolSpeakersSubsection.module.scss';
import type { ISpeakerItemData } from './types';

type TProps = {
  id: number;
  name: string;
};

export const SchoolSpeakersSubsection = ({ id, name }: TProps) => {
  const navigate = useNavigate();

  const {
    data: speakersData,
    isLoading: areSpeakersLoading,
    isFetching: areSpeakersFetching,
    isError: isSpeakersFetchError,
  } = useGetSchoolSpeakersQuery({ schoolId: id });
  const [reorderSpeakers] = useReorderSchoolSpeakersMutation();
  const [deleteSpeaker, { isLoading: isSpeakerDeleting }] = useDeleteSchoolSpeakerMutation();

  const [tableData, setTableData] = useState<ISpeakerItemData[]>([]);

  const [isCreateSpeakerModalOpened, setIsCreateSpeakerModalOpened] = useState(false);
  const [isEditSpeakerModalOpened, setIsEditSpeakerModalOpened] = useState(false);
  const [isDeleteSpeakerModalOpened, setIsDeleteSpeakerModalOpened] = useState(false);

  const [currentSpeakerItem, setCurrentSpeakerItem] = useState<null | ISpeakerItemData>(null);

  const openCreateSpeakerModal = useCallback(() => setIsCreateSpeakerModalOpened(true), []);
  const closeCreateSpeakerModal = useCallback(() => setIsCreateSpeakerModalOpened(false), []);

  const openEditSpeakerModal = useCallback(() => setIsEditSpeakerModalOpened(true), []);
  const closeEditSpeakerModal = useCallback(() => setIsEditSpeakerModalOpened(false), []);

  const openDeleteSpeakerModal = useCallback(() => setIsDeleteSpeakerModalOpened(true), []);
  const closeDeleteSpeakerModal = useCallback(() => setIsDeleteSpeakerModalOpened(false), []);

  const sendApiRequest = useApiRequest();

  const setItemToOperate = useCallback((data: ISpeakerItemData | null) => setCurrentSpeakerItem(data), []);

  const handleWithClearOperateItem = (handleFunction?: VoidFunction) => {
    handleFunction?.();
    setItemToOperate(null);
  };

  const deleteSpeakerHandle = useCallback(() => {
    if (currentSpeakerItem) {
      sendApiRequest({
        successMessage: 'Спикер успешно удалён',
        apiRequestCallback: () =>
          deleteSpeaker({
            schoolId: id,
            speakerId: currentSpeakerItem?.id,
          }),
        successCallback: () => handleWithClearOperateItem(closeDeleteSpeakerModal),
      });
    }
  }, [currentSpeakerItem?.id, id]);

  const columns = useMemo(
    () => getColumnsData({ openEditSpeakerModal, openDeleteSpeakerModal, setItemToOperate }),
    [openEditSpeakerModal, openDeleteSpeakerModal],
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!tableData || !over) {
      return;
    }

    if (active.id !== over.id) {
      const activeIndex = tableData.findIndex((i) => i.position === active.id);
      const overIndex = tableData.findIndex((i) => i.position === over.id);

      const reorderedArray = arrayMove(tableData, activeIndex, overIndex);

      setTableData(reorderedArray);

      const reorderedArrayIds = reorderedArray.map((speaker) => speaker.id);

      sendApiRequest({
        successMessage: 'Порядок спикеров успешно изменён',
        apiRequestCallback: () => reorderSpeakers({ schoolId: id, data: { order: reorderedArrayIds } }),
        errorCallback: () => setTableData(tableData),
      });
    }
  };

  useEffect(() => {
    setTableData(speakersData ?? []);
  }, [speakersData]);

  return (
    <FetchDataWrapper
      isLoading={areSpeakersLoading}
      isError={isSpeakersFetchError}
    >
      {speakersData && (
        <div className={styles.wrapper}>
          <Breadcrumb
            items={[
              { title: 'Публичные страницы' },
              { title: <Link to={`/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}`}>Школы</Link> },
              { title: <Link to={`/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}/${id}/${ROUTES.EDIT}`}>{name}</Link> },
              { title: 'Спикеры' },
            ]}
          />
          <div className={styles['add-button-wrapper']}>
            <Button
              type="primary"
              onClick={openCreateSpeakerModal}
              className={styles.button}
            >
              Добавить спикера
            </Button>
          </div>
          <DraggableTable
            onDragEnd={onDragEnd}
            keysForSorting={tableData.map((i) => i.position)}
            columns={columns}
            rowKey="position"
            size="large"
            locale={TABLE_TRANSLATIONS}
            pagination={false}
            dataSource={tableData}
            loading={areSpeakersFetching}
          />
          <div className={styles['action-buttons-wrapper']}>
            <Button
              onClick={() => navigate(`/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}/${id}/${ROUTES.EDIT}`)}
              type="dashed"
              size="large"
              className={styles.button}
            >
              Назад
            </Button>
          </div>
          {isDeleteSpeakerModalOpened && (
            <DeleteConfirmationModal
              isModalOpen={isDeleteSpeakerModalOpened}
              description="Вы уверены, что хотите удалить спикера?"
              title="Удаление спикера"
              handleCancel={() => handleWithClearOperateItem(closeDeleteSpeakerModal)}
              handleOk={deleteSpeakerHandle}
              itemToOperate={currentSpeakerItem}
              shouldDisableElement={isSpeakerDeleting}
            />
          )}
          {isCreateSpeakerModalOpened && (
            <CreateSpeakerModal
              isModalOpen={isCreateSpeakerModalOpened}
              handleCancel={closeCreateSpeakerModal}
              schoolId={id}
            />
          )}
          {isEditSpeakerModalOpened && currentSpeakerItem && (
            <EditSpeakerModal
              isModalOpen={isEditSpeakerModalOpened}
              handleCancel={() => handleWithClearOperateItem(closeEditSpeakerModal)}
              handleClearOperateItem={() => handleWithClearOperateItem()}
              itemToOperate={currentSpeakerItem}
              schoolId={id}
            />
          )}
        </div>
      )}
    </FetchDataWrapper>
  );
};
